'use client';

import React, { useEffect, useRef } from 'react';
import Link from 'next/link';

const demos = [
  {
    role: 'Property Manager',
    tagline: 'Full portfolio command center',
    description: 'Manage properties, track leases, monitor rent collection, and oversee maintenance across your entire portfolio from one dashboard.',
    href: '/dashboard/property-manager',
    accent: '#A78BFA',
    accentBg: 'rgba(124,58,237,0.08)',
    accentBorder: 'rgba(124,58,237,0.25)',
    badge: 'PM',
    badgeBg: 'rgba(124,58,237,0.2)',
    stats: [
      { label: 'Properties', value: '12' },
      { label: 'Units', value: '272' },
      { label: 'Occupancy', value: '91%' },
    ],
    features: ['Portfolio overview', 'Lease expirations', 'Maintenance queue', 'Rent tracking'],
  },
  {
    role: 'Staff',
    tagline: 'Maintenance & operations hub',
    description: 'View assigned work orders, manage daily schedules, coordinate with vendors, and log completed maintenance tasks efficiently.',
    href: '/dashboard/staff',
    accent: '#F59E0B',
    accentBg: 'rgba(245,158,11,0.08)',
    accentBorder: 'rgba(245,158,11,0.25)',
    badge: 'SM',
    badgeBg: 'rgba(245,158,11,0.15)',
    stats: [
      { label: 'Open Tickets', value: '4' },
      { label: 'Scheduled', value: '4' },
      { label: 'Avg Resolution', value: '1.4d' },
    ],
    features: ['Work order queue', 'Daily schedule', 'Vendor directory', 'Priority routing'],
  },
  {
    role: 'Tenant',
    tagline: 'Resident self-service portal',
    description: 'Pay rent, submit maintenance requests, review lease details, and communicate with property management — all in one place.',
    href: '/dashboard/tenant',
    accent: '#22C55E',
    accentBg: 'rgba(34,197,94,0.06)',
    accentBorder: 'rgba(34,197,94,0.2)',
    badge: 'T',
    badgeBg: 'rgba(34,197,94,0.12)',
    stats: [
      { label: 'Next Due', value: 'Mar 1' },
      { label: 'On-Time', value: '100%' },
      { label: 'Open Requests', value: '2' },
    ],
    features: ['Rent payments', 'Maintenance requests', 'Lease details', 'Messaging'],
  },
  {
    role: 'Super Admin',
    tagline: 'Platform-wide control center',
    description: 'Monitor all accounts, track platform MRR, manage system health, review infrastructure alerts, and oversee every tenant organization.',
    href: '/dashboard/super-admin',
    accent: '#EF4444',
    accentBg: 'rgba(239,68,68,0.06)',
    accentBorder: 'rgba(239,68,68,0.2)',
    badge: 'SA',
    badgeBg: 'rgba(239,68,68,0.12)',
    stats: [
      { label: 'Users', value: '12,847' },
      { label: 'MRR', value: 'R1,984K' },
      { label: 'Uptime', value: '99.98%' },
    ],
    features: ['Account management', 'Revenue analytics', 'System alerts', 'Infrastructure'],
  },
];

export default function DemosSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.querySelectorAll('.reveal-blur').forEach((el, i) => {
              setTimeout(() => el.classList.add('is-visible'), i * 100);
            });
            observer.unobserve(e.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section id="demos" ref={sectionRef} className="py-16 sm:py-24 px-4 sm:px-6 relative">
      {/* Background glow */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse 70% 50% at 50% 50%, rgba(124,58,237,0.06) 0%, transparent 70%)',
        }}
      />

      <div className="max-w-7xl mx-auto relative">
        {/* Header */}
        <div className="mb-14 reveal-blur">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[rgba(124,58,237,0.25)] mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-[#A78BFA]" />
            <span className="text-[10px] font-mono text-[#A78BFA] tracking-widest uppercase">Live Demos</span>
          </div>
          <h2
            className="font-manrope font-extrabold tracking-tighter text-[#EEEEF0] leading-tight max-w-3xl"
            style={{ fontSize: 'clamp(2rem, 4vw, 3.25rem)' }}
          >
            Every role, its own dashboard.
          </h2>
          <p className="text-[#9999A8] text-lg mt-4 max-w-xl">
            Explore purpose-built interfaces for each stakeholder — from the tenant paying rent to the admin running the platform.
          </p>
        </div>

        {/* Demo cards — asymmetric bento */}
        <div className="grid grid-cols-12 gap-4">
          {/* Property Manager — large card, 7 cols */}
          <div className="col-span-12 lg:col-span-7 reveal-blur">
            <DemoCard demo={demos[0]} large />
          </div>

          {/* Staff Member — 5 cols */}
          <div className="col-span-12 lg:col-span-5 reveal-blur">
            <DemoCard demo={demos[1]} />
          </div>

          {/* Tenant — 5 cols */}
          <div className="col-span-12 lg:col-span-5 reveal-blur">
            <DemoCard demo={demos[2]} />
          </div>

          {/* Super Admin — 7 cols */}
          <div className="col-span-12 lg:col-span-7 reveal-blur">
            <DemoCard demo={demos[3]} large />
          </div>
        </div>
      </div>
    </section>
  );
}

interface DemoCardProps {
  demo: typeof demos[0];
  large?: boolean;
}

function DemoCard({ demo }: DemoCardProps) {
  return (
    <Link href={demo.href} className="block group h-full">
      <div
        className="bento-card card-shine h-full p-5 sm:p-7 flex flex-col gap-5 cursor-pointer transition-all duration-300"
        style={{
          background: demo.accentBg,
          borderColor: demo.accentBorder,
        }}
      >
        {/* Top row */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div
              className="w-15 h-15 rounded-lg flex items-center justify-center font-manrope font-bold text-sm shrink-0"
              style={{ background: demo.badgeBg, color: demo.accent, border: `1px solid ${demo.accentBorder}` }}
            >
              {demo.badge}
            </div>
            <div>
              <div className="text-[24px] font-bold tracking-widest uppercase mb-0.2" style={{ color: demo.accent }}>{demo.role}</div>
              <div className="font-manrope font-bold text-base text-[#EEEEF0] tracking-tight">{demo.tagline}</div>
            </div>
          </div>
          <div
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-mono shrink-0 transition-all duration-200 group-hover:gap-2.5"
            style={{ background: demo.badgeBg, color: demo.accent, border: `1px solid ${demo.accentBorder}` }}
          >
            View Demo
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="transition-transform duration-200 group-hover:translate-x-0.5">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </div>
        </div>

        {/* Description */}
        <p className="text-sm text-[#9999A8] leading-relaxed">{demo.description}</p>

        {/* Stats row */}
        <div className="grid grid-cols-3 gap-3">
          {demo.stats.map((s) => (
            <div key={s.label} className="demo-stat-tile rounded-lg p-3 border border-[rgba(255,255,255,0.04)]">
              <div className="font-manrope font-bold text-3xl tracking-tight" style={{ color: demo.accent }}>{s.value}</div>
              <div className="text-[14px] font-mono text-[#5A5A6E] mt-0.5">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Feature tags */}
        <div className="flex flex-wrap gap-2 mt-auto">
          {demo.features.map((f) => (
            <span
              key={f}
              className="px-2.5 py-1 rounded-full text-[10px] font-mono"
              style={{ background: 'rgba(255,255,255,0.04)', color: '#9999A8', border: '1px solid rgba(255,255,255,0.06)' }}
            >
              {f}
            </span>
          ))}
        </div>
      </div>
    </Link>
  );
}
